import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Icon from "../../components/AppIcon";
import ClustersTab from "./components/ClustersTab";
import TeamsTab from "./components/TeamsTab";
import TeamMembersTab from "./components/TeamMembersTab";
import {
  clusters as clustersAPI,
  users as usersAPI,
  projects as projectsAPI,
  regions as regionsAPI,
  teams as teamsAPI,
} from "../../lib/database";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { hashPassword } from "../../lib/password";

const ClusterManagement = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [clusters, setClusters] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [regions, setRegions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [newUserUniqueId, setNewUserUniqueId] = useState(null);
  const [activeTab, setActiveTab] = useState("clusters");
  const [selectedProject, setSelectedProject] = useState(null);

  // Listen for project selection changes (for super_admin)
  useEffect(() => {
    const updateSelectedProject = () => {
      const project = localStorage.getItem("selectedProject");
      setSelectedProject(project && project !== "all" ? project : null);
    };

    // Initial load
    updateSelectedProject();

    // Listen for storage changes (when project is selected in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === "selectedProject") {
        updateSelectedProject();
      }
    };

    // Listen for custom event (when project is selected in same tab)
    const handleProjectChange = () => {
      updateSelectedProject();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("projectChanged", handleProjectChange);

    // Poll localStorage periodically to catch changes in same tab
    const interval = setInterval(updateSelectedProject, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("projectChanged", handleProjectChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [user, selectedProject]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Determine project filter based on role
      // super_admin: use selectedProject from header if set
      // project_admin: always use their own project_id
      const projectId =
        user?.role === "super_admin" && selectedProject
          ? selectedProject
          : user?.role === "project_admin" && user?.project_id
            ? user.project_id
            : null;

      let clustersData, usersData, teamsData;

      // Always fetch all projects for the dropdowns
      const projectsData = await projectsAPI.getAll();
      setProjects(projectsData || []);

      // Fetch regions (project admins see their project's regions, super admin sees selected project's or all)
      let regionsData;
      if (projectId) {
        regionsData = await regionsAPI.getByProjectId(projectId);
      } else {
        regionsData = await regionsAPI.getAll();
      }
      setRegions(regionsData || []);

      // Fetch filtered data based on projectId
      if (projectId) {
        // Fetch data filtered by project
        [clustersData, usersData, teamsData] = await Promise.all([
          clustersAPI.getByProjectId(projectId),
          usersAPI.getByProjectId(projectId),
          teamsAPI.getByProjectId(projectId),
        ]);
      } else {
        // No project filter - get all data
        [clustersData, usersData, teamsData] = await Promise.all([
          clustersAPI.getAll(),
          usersAPI.getAll(),
          teamsAPI.getAll(),
        ]);
      }

      setClusters(clustersData || []);
      setTeams(teamsData || []);

      // Map users to include cluster name from relation (show all staff users)
      const mappedUsers = (usersData || [])
        .filter((u) => u.category === "staff")
        .map((u) => ({
          ...u,
          clusterName: u.clusters?.name || null,
        }));
      setTeamMembers(mappedUsers);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCluster = async (newCluster) => {
    try {
      setError(null);
      const createdCluster = await clustersAPI.create(newCluster);
      setClusters([createdCluster, ...clusters]);
    } catch (err) {
      console.error("Error creating cluster:", err);
      setError(err.message || "Failed to create cluster");
      throw err;
    }
  };

  const handleUpdateCluster = async (clusterId, updatedData) => {
    try {
      setError(null);
      const updatedCluster = await clustersAPI.update(clusterId, updatedData);
      setClusters(
        clusters.map((cluster) =>
          cluster.id === clusterId ? updatedCluster : cluster,
        ),
      );
    } catch (err) {
      console.error("Error updating cluster:", err);
      setError(err.message || "Failed to update cluster");
      throw err;
    }
  };

  const handleDeleteCluster = async (clusterId) => {
    try {
      setError(null);
      await clustersAPI.delete(clusterId);
      setClusters(clusters.filter((cluster) => cluster.id !== clusterId));
      // Also remove teams associated with this cluster
      setTeams(teams.filter((team) => team.cluster_id !== clusterId));
    } catch (err) {
      console.error("Error deleting cluster:", err);
      setError(err.message || "Failed to delete cluster");
      throw err;
    }
  };

  // Generate random password
  const generateRandomPassword = () => {
    const length = 8;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // Generate unique_id based on role
  const generateUniqueId = async (role) => {
    // Call database function to generate unique ID via RPC
    const { data, error } = await supabase.rpc("generate_unique_user_id", {
      role_type: role,
    });
    if (error) {
      throw error;
    }
    return data;
  };

  const handleAddMember = async (memberData) => {
    try {
      setError(null);
      setGeneratedPassword(null);
      setNewUserUniqueId(null);

      // Generate random password
      const password = generateRandomPassword();

      // Determine category based on role
      const role = memberData.role || "worker";
      const category =
        role === "super_admin" || role === "project_admin" ? "admin" : "staff";

      // Generate unique_id
      const uniqueId = await generateUniqueId(role);

      // Normalize team_id: convert empty string to null/undefined
      const teamId =
        memberData.team_id && memberData.team_id.trim() !== ""
          ? memberData.team_id
          : null;

      // Get project_id from the selected cluster (for all roles except super_admin)
      let projectId = null;
      if (role !== "super_admin" && memberData.cluster_id) {
        const selectedCluster = clusters.find(
          (c) => c.id === memberData.cluster_id,
        );
        projectId = selectedCluster?.project_id || null;
      }

      // Create user directly in users table with team_id and project_id (password stored hashed)
      const newUser = await usersAPI.create({
        unique_id: uniqueId,
        password: hashPassword(password),
        role: role,
        category: category,
        name: memberData.name,
        cluster_id: memberData.cluster_id || null,
        team_id: teamId,
        project_id: projectId,
      });

      // Store generated password and unique_id to display
      setGeneratedPassword(password);
      setNewUserUniqueId(uniqueId);

      // Refresh data
      await fetchData();
      return { ...newUser, generatedPassword: password };
    } catch (err) {
      console.error("Error creating team member:", err);
      setError(err.message || "Failed to create team member");
      throw err;
    }
  };

  const handleUpdateMember = async (memberId, updatedData) => {
    try {
      setError(null);

      // Normalize team_id: convert empty string to null/undefined
      const teamId =
        updatedData.team_id && updatedData.team_id.trim() !== ""
          ? updatedData.team_id
          : null;

      // Get the current user to check their role if role is not in updatedData
      const currentMember = teamMembers.find((m) => m.id === memberId);
      const role = updatedData.role || currentMember?.role;

      // Get project_id from the selected cluster (for all roles except super_admin)
      let projectId = undefined; // undefined means don't update project_id
      if (role === "super_admin") {
        // If role is super_admin, ensure project_id is null
        projectId = null;
      } else if (updatedData.cluster_id) {
        // If role is not super_admin and cluster_id is provided, set project_id from cluster
        const selectedCluster = clusters.find(
          (c) => c.id === updatedData.cluster_id,
        );
        projectId = selectedCluster?.project_id || null;
      }

      // Build update object
      const updatePayload = {
        ...updatedData,
        team_id: teamId,
      };

      // Only include project_id if we determined it should be updated
      if (projectId !== undefined) {
        updatePayload.project_id = projectId;
      }

      // Update user with team_id and project_id directly in users table
      const updatedUser = await usersAPI.update(memberId, updatePayload);

      // Refresh data to get updated information
      await fetchData();
      return updatedUser;
    } catch (err) {
      console.error("Error updating team member:", err);
      setError(err.message || "Failed to update team member");
      throw err;
    }
  };

  const handleDeleteMember = async (memberId) => {
    try {
      setError(null);
      await usersAPI.delete(memberId);
      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error deleting team member:", err);
      setError(err.message || "Failed to delete team member");
      throw err;
    }
  };

  const handleChangePassword = async (memberId, newPassword) => {
    try {
      setError(null);
      await usersAPI.update(memberId, {
        password: hashPassword(newPassword),
      });
      await fetchData();
    } catch (err) {
      console.error("Error changing password:", err);
      setError(err.message || "Failed to change password");
      throw err;
    }
  };

  const handleCreateOrUpdateTeam = async (teamPayload) => {
    const { id, name, cluster_id, user_ids } = teamPayload;
    try {
      setError(null);

      // Create or update team
      const team = id
        ? await teamsAPI.update(id, {
            name,
            cluster_id: cluster_id || null,
          })
        : await teamsAPI.create({
            name,
            cluster_id: cluster_id || null,
          });

      // Update users' team_id directly
      // First, remove team_id from users who were previously in this team but are not in the new list
      const previousMembers = teamMembers.filter(
        (member) => member.team_id === team.id,
      );
      const previousMemberIds = previousMembers.map((m) => m.id);
      const removedMemberIds = previousMemberIds.filter(
        (memberId) => !user_ids.includes(memberId),
      );

      // Remove team_id from users no longer in this team
      for (const userId of removedMemberIds) {
        await usersAPI.update(userId, { team_id: null });
      }

      // Add team_id to new members
      const newUserIds = user_ids || [];
      for (const userId of newUserIds) {
        await usersAPI.update(userId, { team_id: team.id });
      }

      await fetchData();
      return team;
    } catch (err) {
      console.error("Error saving team:", err);
      setError(err.message || "Failed to save team");
      throw err;
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      setError(null);
      await teamsAPI.delete(teamId);
      await fetchData();
    } catch (err) {
      console.error("Error deleting team:", err);
      setError(err.message || "Failed to delete team");
      throw err;
    }
  };

  return (
    <>
      <Helmet>
        <title>Cluster Management - Solar Installation Tracker</title>
        <meta
          name="description"
          content="Manage installation clusters and view team assignments"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Cluster Management
              </h1>
              <p className="text-muted-foreground">
                Manage installation clusters, teams, and team members
              </p>
              {error && (
                <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading data...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Tabs - horizontal scroll on mobile so tabs don't overflow */}
                <div className="bg-card border border-border rounded-lg">
                  <div className="overflow-x-auto -mx-px px-px md:overflow-visible md:mx-0 md:px-0">
                    <div className="flex border-b border-border flex-nowrap min-w-0 md:flex-wrap">
                      <button
                        onClick={() => setActiveTab("clusters")}
                        className={`flex-shrink-0 min-w-[100px] md:min-w-0 md:flex-1 px-4 md:px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                          activeTab === "clusters"
                            ? "text-primary border-b-2 border-primary bg-primary/5"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                          <Icon name="MapPin" size={18} />
                          <span>Clusters</span>
                          <span className="ml-1 md:ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                            {clusters?.length || 0}
                          </span>
                        </div>
                      </button>
                      <button
                        onClick={() => setActiveTab("teams")}
                        className={`flex-shrink-0 min-w-[100px] md:min-w-0 md:flex-1 px-4 md:px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                          activeTab === "teams"
                            ? "text-primary border-b-2 border-primary bg-primary/5"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                          <Icon name="UserCog" size={18} />
                          <span>Teams</span>
                          <span className="ml-1 md:ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                            {teams?.length || 0}
                          </span>
                        </div>
                      </button>
                      <button
                        onClick={() => setActiveTab("members")}
                        className={`flex-shrink-0 min-w-[100px] md:min-w-0 md:flex-1 px-4 md:px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                          activeTab === "members"
                            ? "text-primary border-b-2 border-primary bg-primary/5"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                          <Icon name="Users" size={18} />
                          <span>Team Members</span>
                          <span className="ml-1 md:ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                            {teamMembers?.length || 0}
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                <div>
                  {activeTab === "clusters" && (
                    <ClustersTab
                      clusters={clusters}
                      projects={projects}
                      teams={teams}
                      teamMembers={teamMembers}
                      onAddCluster={handleAddCluster}
                      onUpdateCluster={handleUpdateCluster}
                      onDeleteCluster={handleDeleteCluster}
                    />
                  )}
                  {activeTab === "teams" && (
                    <TeamsTab
                      teams={teams}
                      clusters={clusters}
                      teamMembers={teamMembers}
                      onSaveTeam={handleCreateOrUpdateTeam}
                      onDeleteTeam={handleDeleteTeam}
                    />
                  )}
                  {activeTab === "members" && (
                    <TeamMembersTab
                      teamMembers={teamMembers}
                      clusters={clusters}
                      teams={teams}
                      onAddMember={handleAddMember}
                      onUpdateMember={handleUpdateMember}
                      onDeleteMember={handleDeleteMember}
                      onChangePassword={handleChangePassword}
                      canChangePassword={isSuperAdmin}
                      generatedPassword={generatedPassword}
                      newUserUniqueId={newUserUniqueId}
                      onClearPassword={() => {
                        setGeneratedPassword(null);
                        setNewUserUniqueId(null);
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default ClusterManagement;
